import { Box, Flex, Heading, Icon, Spinner, Text } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FiAlertCircle } from "react-icons/fi";
import { cyberidVerify } from "@lubbezposrednio-frontend/core";
import { CYBERID_VERIFY_REDIRECT_URI, consumeCyberidVerifyState, storeCyberidAssertion } from "../../utils/cyberidAuth";

type Phase = "working" | "error";

/**
 * Powrót z weryfikacji tożsamości CyberID (per głosowanie). Wymienia code na asercję (BEZ sesji/konta),
 * zapisuje ją i wraca na stronę głosowania, gdzie wyborca dokańcza oddanie głosu.
 */
export const CyberidVerifyCallbackView: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("working");
  const [errorMsg, setErrorMsg] = useState("");
  const ran = useRef(false); // StrictMode: nie wymieniaj jednorazowego code dwa razy

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    document.title = "CyberID — weryfikacja";

    const code = params.get("code");
    const oauthError = params.get("error");
    const ctx = consumeCyberidVerifyState(params.get("state"));

    if (oauthError || !code || !ctx) {
      setPhase("error");
      return;
    }

    (async () => {
      try {
        const res = await cyberidVerify(code, ctx.verifier, CYBERID_VERIFY_REDIRECT_URI, ctx.nonce, ctx.votingId);
        const { assertion, email } = res.data;
        storeCyberidAssertion(ctx.votingId, assertion, email);
        navigate(`/glosowanie/${ctx.votingId}`, { replace: true });
      } catch (e) {
        const err = e as { response?: { data?: { errors?: string[] } } };
        setErrorMsg(err?.response?.data?.errors?.[0] ?? "");
        setPhase("error");
      }
    })();
  }, [params, navigate]);

  const card = (children: React.ReactNode) => (
    <Flex minH="100vh" bg="#F7F5FB" align="center" justify="center" px="20px">
      <Box w="100%" maxW="440px" bg="white" borderRadius="22px" boxShadow="0 18px 50px rgba(67,0,152,0.10)" border="1px solid rgba(67,0,152,0.06)" p={{ base: "32px 24px", md: "40px" }} textAlign="center">
        {children}
      </Box>
    </Flex>
  );

  if (phase === "working") {
    return card(
      <>
        <Spinner color="primary" size="lg" thickness="3px" mb="18px" />
        <Text color="gray.500">Weryfikacja tożsamości przez CyberID…</Text>
      </>
    );
  }

  return card(
    <>
      <Icon as={FiAlertCircle} boxSize="44px" color="#e51f44" mb="14px" />
      <Heading fontSize="21px" color="primary" mb="8px">
        Weryfikacja nie powiodła się
      </Heading>
      <Text fontSize="14px" color="gray.500" mb="22px">
        {errorMsg || "Weryfikacja tożsamości przez CyberID została przerwana lub wygasła. Spróbuj ponownie ze strony głosowania."}
      </Text>
      <Text as={Link} to="/aktywne-glosowania" fontSize="14px" color="accent" fontWeight="700">
        Wróć do głosowań
      </Text>
    </>
  );
};
